import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown } from "lucide-react";

// Travel-relevant SocialPulse sections, in display order.
const SECTIONS = [
  ["overview", "Overview"],
  ["history", "History"],
  ["geography", "Geography"],
  ["weather", "Weather & climate"],
  ["transportation", "Getting around"],
  ["tourism", "Tourism & attractions"],
  ["recreation", "Recreation"],
  ["parks_nature", "Parks & nature"],
  ["arts_culture", "Arts & culture"],
  ["religion", "Religion"],
  ["sports", "Sports"],
  ["economy", "Economy"],
  ["demographics", "Demographics"],
  ["neighborhoods", "Neighbourhoods"],
  ["events", "Events"],
  ["facts", "Interesting facts"],
  ["faq", "FAQ"],
];

const isReal = (v) =>
  typeof v === "string" && v.trim().length > 60 && !/coming soon/i.test(v);

const MD = {
  h1: (p) => <h3 className="font-heading font-700 text-lg mt-3 mb-1" {...p} />,
  h2: (p) => <h3 className="font-heading font-700 text-lg mt-3 mb-1" {...p} />,
  h3: (p) => <h4 className="font-heading font-600 text-base mt-2 mb-1" {...p} />,
  p: (p) => <p className="text-base leading-relaxed text-zinc-700 mb-2" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-1 text-base text-zinc-700 mb-2" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-1 text-base text-zinc-700 mb-2" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-700 text-zinc-900" {...p} />,
  a: (p) => <a className="text-primary hover:underline" target="_blank" rel="noreferrer" {...p} />,
  table: (p) => <table className="w-full text-sm border-collapse my-3" {...p} />,
  th: (p) => <th className="border border-zinc-300 bg-zinc-50 px-2 py-1 text-left" {...p} />,
  td: (p) => <td className="border border-zinc-300 px-2 py-1" {...p} />,
};

function Section({ title, text, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-300 bg-white" data-testid={`guide-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50">
        <span className="font-heading font-700 text-lg">{title}</span>
        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-zinc-100 pt-3">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{text}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

export default function GuideSections({ guide }) {
  const s = guide?.sections || {};
  const present = SECTIONS.filter(([k]) => isReal(s[k]));
  if (present.length === 0) return null;

  const refs = Array.isArray(guide?.links) ? guide.links : [];

  return (
    <div className="space-y-3" data-testid="guide-sections">
      {present.map(([k, label], i) => (
        <Section key={k} title={label} text={s[k]} defaultOpen={i < 2} />
      ))}
      {refs.length > 0 && (
        <div className="border border-zinc-300 bg-white p-4">
          <div className="font-heading font-700 text-lg mb-2">References</div>
          <ul className="space-y-1">
            {refs.map((l, i) => (
              <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm">{l.caption || l.url}</a></li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-muted-foreground">City guide content from the SocialPulse network.</p>
    </div>
  );
}
